import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Mail, Linkedin, MapPin } from "lucide-react";
import { profile } from "../data/portfolio";

const ease = [0.16, 1, 0.3, 1];

function ContactBackdrop() {
  return (
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgb(var(--ink)/0.06),transparent_60%)] pointer-events-none" />
  );
}

function ContactIntro({ compact = false }) {
  return (
    <>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8, ease }}
        className="font-body text-[10px] md:text-xs tracking-[0.4em] uppercase text-ink-2 mb-8 md:mb-10"
      >
        07 — Contact
      </motion.p>

      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1, ease, delay: 0.1 }}
        className={`font-display tracking-[-0.05em] leading-[0.9] text-ink ${
          compact
            ? "text-5xl sm:text-6xl"
            : "text-7xl md:text-9xl lg:text-[180px]"
        }`}
      >
        Let&apos;s
        <br />
        <span className="text-metal italic font-light">build.</span>
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.9, ease, delay: 0.25 }}
        className="mt-8 md:mt-10 max-w-xl font-body text-base md:text-lg text-ink-2 leading-relaxed"
      >
        Hiring for AI, automation, or full-stack work? I&apos;d love to hear about
        the problem before we talk about the stack. Drop a line.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.9, ease, delay: 0.35 }}
        className="mt-8 md:mt-12 flex flex-col sm:flex-row flex-wrap gap-3 md:gap-4"
      >
        <a
          href={`mailto:${profile.email}`}
          data-testid="footer-cta-email"
          className="inline-flex items-center justify-center gap-2 px-6 h-12 rounded-full bg-ink text-surface font-body text-sm font-medium hover:bg-ink-metal transition-all duration-300 active:scale-95"
        >
          <Mail className="w-4 h-4 shrink-0" />
          <span className="truncate">{profile.email}</span>
        </a>
        <a
          href={profile.linkedin}
          target="_blank"
          rel="noreferrer"
          data-testid="footer-cta-linkedin"
          className="inline-flex items-center justify-center gap-2 px-6 h-12 rounded-full border border-line/15 text-ink font-body text-sm font-medium hover:bg-line/5 hover:border-line/30 transition-all duration-300"
        >
          <Linkedin className="w-4 h-4" />
          LinkedIn
          <ArrowUpRight className="w-3.5 h-3.5" />
        </a>
      </motion.div>
    </>
  );
}

function ContactMeta({ showWatermark = true }) {
  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 1, ease, delay: 0.2 }}
        className="grid grid-cols-2 gap-x-6 gap-y-8 md:grid-cols-12 md:gap-6 text-ink-2"
      >
        <div className="col-span-1 md:col-span-3">
          <p className="font-body text-[10px] tracking-[0.3em] uppercase mb-3">
            Based
          </p>
          <p className="font-body text-sm text-ink-metal inline-flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5 shrink-0" />
            {profile.location}
          </p>
        </div>
        <div className="col-span-1 md:col-span-3">
          <p className="font-body text-[10px] tracking-[0.3em] uppercase mb-3">
            Reach
          </p>
          <a
            href={`mailto:${profile.email}`}
            data-testid="footer-meta-email"
            className="font-body text-sm text-ink-metal hover:text-ink inline-flex items-center gap-2 transition-colors break-all"
          >
            <Mail className="w-3.5 h-3.5 shrink-0" />
            {profile.email}
          </a>
        </div>
        <div className="col-span-1 md:col-span-3">
          <p className="font-body text-[10px] tracking-[0.3em] uppercase mb-3">
            Status
          </p>
          <p className="font-body text-sm text-ink-metal inline-flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.6)]" />
            Open to new opportunities
          </p>
        </div>
        <div className="col-span-1 md:col-span-3 md:text-right">
          <p className="font-body text-[10px] tracking-[0.3em] uppercase mb-3">
            © 2026
          </p>
          <p className="font-body text-sm text-ink-metal">
            G-Folio · Geetanshu Kumboj
          </p>
        </div>
      </motion.div>

      {showWatermark && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 1, ease, delay: 0.35 }}
          className="mt-12 md:mt-32 overflow-hidden"
        >
          <p className="font-display text-[18vw] md:text-[14vw] leading-[0.85] tracking-[-0.06em] text-ink/[0.04] whitespace-nowrap select-none">
            GEETANSHU&nbsp;KUMBOJ
          </p>
        </motion.div>
      )}
    </>
  );
}

function MobileIntroSnap() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], [80, -80]);

  return (
    <div
      ref={ref}
      data-snap-page
      data-testid="footer-intro-snap"
      className="md:hidden relative h-[100svh] overflow-hidden"
    >
      <ContactBackdrop />
      <motion.div
        aria-hidden
        style={{ y: bgY }}
        className="pointer-events-none absolute right-[-4vw] top-1/2 -translate-y-1/2 font-display text-[42vw] leading-none tracking-[-0.06em] text-ink/[0.04] select-none"
      >
        07
      </motion.div>
      <div className="relative h-full max-w-[1400px] mx-auto px-6 pt-24 pb-10 flex flex-col justify-center">
        <ContactIntro compact />
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease, delay: 0.5 }}
          className="mt-10 font-body text-[10px] tracking-[0.3em] uppercase text-ink-2"
        >
          Scroll for details ↓
        </motion.p>
      </div>
    </div>
  );
}

function MobileMetaSnap() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <div
      ref={ref}
      data-snap-page
      data-testid="footer-meta-snap"
      className="md:hidden relative h-[100svh] overflow-hidden"
    >
      <ContactBackdrop />
      <motion.div
        aria-hidden
        style={{ y: bgY }}
        className="pointer-events-none absolute left-[-4vw] bottom-8 font-display text-[28vw] leading-none tracking-[-0.06em] text-ink/[0.04] select-none"
      >
        contact
      </motion.div>
      <div className="relative h-full max-w-[1400px] mx-auto px-6 pt-24 pb-10 flex flex-col justify-center">
        <p className="font-body text-[10px] tracking-[0.4em] uppercase text-ink-2 mb-8">
          07 — Contact · Details
        </p>
        <ContactMeta />
      </div>
    </div>
  );
}

function DesktopFooterSnap() {
  return (
    <div
      data-snap-page
      data-testid="footer-desktop-snap"
      className="hidden md:block relative h-[100svh] overflow-hidden"
    >
      <ContactBackdrop />
      <div className="relative max-w-[1400px] mx-auto px-10 py-32 h-full flex flex-col justify-center">
        <ContactIntro />
        <div className="mt-20 pt-10 border-t border-line/5">
          <ContactMeta />
        </div>
      </div>
    </div>
  );
}

export default function Footer() {
  return (
    <section
      id="contact"
      data-testid="footer-section"
      className="relative bg-surface"
    >
      <MobileIntroSnap />
      <MobileMetaSnap />
      <DesktopFooterSnap />
    </section>
  );
}
